'use strict';

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BarChart3, Download, FileSpreadsheet, Loader2 } from 'lucide-react'
import {
  fetchBooksCompanies,
  fetchBooksReport,
  exportBooksReport,
  type BooksReportPayload,
  type BooksReportDisplayRow,
  type BooksCompany,
} from '../../../api/accounting'

const REPORTS = [
  { id: 'profit_loss', label: 'P&L' },
  { id: 'balance_sheet', label: 'Balance Sheet' },
  { id: 'cash_flow', label: 'Cash Flow' },
] as const

const PERIODS = [
  { id: 'ytd', label: 'YTD' },
  { id: 'month', label: 'This month' },
  { id: 'quarter', label: 'This quarter' },
  { id: 'custom', label: 'Custom' },
] as const

function money(n: number | string | undefined) {
  const v = parseFloat(String(n ?? 0)) || 0
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function bkReady(payload?: BooksReportPayload) {
  const t = payload?.bkTrust
  return t?.status === 'ready' || t?.cache === 'hit' || t?.cache === 'stale'
}

function bkUnavailable(payload?: BooksReportPayload) {
  const t = payload?.bkTrust
  return t?.status === 'unavailable' || t?.cache === 'error'
}

function bkPaused(payload?: BooksReportPayload) {
  return payload?.bkTrust?.status === 'paused' || payload?.bkTrust?.cache === 'paused'
}

function bkPending(payload?: BooksReportPayload) {
  return !!payload && !bkReady(payload) && !bkUnavailable(payload)
}

function rowLabel(row: BooksReportDisplayRow) {
  return row.displayName || row.name
}

function varianceClass(hasVariance?: boolean) {
  return hasVariance ? 'books-variance' : ''
}

function exportParams(activeCompany: string, report: string, period: string, basis: string,
  projectId: string, customStart: string, customEnd: string) {
  return {
    company: activeCompany,
    report,
    period,
    basis,
    projectId: projectId || undefined,
    startDate: period === 'custom' ? customStart : undefined,
    endDate: period === 'custom' ? customEnd : undefined,
  }
}

export default function BooksReportsPanel() {
  const [searchParams] = useSearchParams()
  const showCrosscheck = searchParams.get('crosscheck') === '1'
  const companies = useQuery({ queryKey: ['books-companies'], queryFn: fetchBooksCompanies })
  const [company, setCompany] = useState<string>('')
  const [report, setReport] = useState<string>('profit_loss')
  const [period, setPeriod] = useState<string>('ytd')
  const [basis, setBasis] = useState<'cash' | 'accrual'>('accrual')
  const [projectId, setProjectId] = useState<string>('')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [exportBusy, setExportBusy] = useState<'csv' | 'pdf' | null>(null)
  const [exportErr, setExportErr] = useState<string | null>(null)

  const companyList = companies.data ?? []
  const activeCompany = company || companyList[0]?.label || ''

  const queryKey = useMemo(() => ([
    'books-report', activeCompany, report, period, basis, projectId, customStart, customEnd,
  ]), [activeCompany, report, period, basis, projectId, customStart, customEnd])

  const reportQ = useQuery({
    queryKey,
    queryFn: () => fetchBooksReport(exportParams(activeCompany, report, period, basis, projectId, customStart, customEnd)),
    enabled: !!activeCompany && (period !== 'custom' || (!!customStart && !!customEnd)),
    refetchInterval: (q) => {
      const d = q.state.data as BooksReportPayload | undefined
      if (bkReady(d) || bkUnavailable(d) || bkPaused(d)) return false
      return 15_000
    },
  })

  const payload = reportQ.data as BooksReportPayload | undefined
  const isSentio = payload?.company === 'Sentio'
  const bkLoaded = bkReady(payload)
  const bkError = bkUnavailable(payload)
  const bkLimitPaused = bkPaused(payload)
  const preparingBk = bkPending(payload)
  const bkHeader = payload?.bkTrust?.label ?? 'Bookeeping.ai'

  async function handleExport(format: 'csv' | 'pdf') {
    setExportErr(null)
    setExportBusy(format)
    try {
      const { blob, filename } = await exportBooksReport({ ...exportParams(
        activeCompany, report, period, basis, projectId, customStart, customEnd,
      ), format })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (ex) {
      setExportErr(ex instanceof Error ? ex.message : 'Export failed')
    } finally {
      setExportBusy(null)
    }
  }

  const displayRows: BooksReportDisplayRow[] = payload?.displayRows?.length
    ? payload.displayRows
    : (payload?.rows ?? []).map(r => ({
        rowType: 'line' as const,
        section: r.section,
        name: r.name,
        displayName: r.displayName || r.name,
        aria: r.aria,
        bk: r.bk,
        variance: r.variance,
        hasVariance: r.hasVariance,
      }))

  return (
    <div className="books-reports" data-testid="books-reports">
      <div className="set-panel-h" style={{ marginBottom: 12 }}>
        <BarChart3 size={15} />
        <div>
          <div className="set-panel-tt">Books — Reports</div>
          <div className="set-panel-st">
            Soren-computed financials from Bookeeping.ai. Compare against Bookeeping category totals.
          </div>
        </div>
      </div>

      <div className="books-toolbar">
        <label className="fld-l">Company</label>
        <select className="fld-i books-select" value={activeCompany}
          onChange={e => setCompany(e.target.value)} data-testid="books-company">
          {companyList.map((c: BooksCompany) => (
            <option key={c.id} value={c.label}>{c.label}</option>
          ))}
          {!companyList.length && <option value="">No Bookeeping connection</option>}
        </select>

        <label className="fld-l">Report</label>
        <div className="books-seg">
          {REPORTS.map(r => (
            <button key={r.id} type="button"
              className={`books-seg-btn${report === r.id ? ' on' : ''}`}
              onClick={() => setReport(r.id)}>{r.label}</button>
          ))}
        </div>

        <label className="fld-l">Period</label>
        <select className="fld-i books-select" value={period} onChange={e => setPeriod(e.target.value)}>
          {PERIODS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === 'custom' && (
          <div className="books-custom-range">
            <input type="date" className="fld-i" value={customStart} onChange={e => setCustomStart(e.target.value)} />
            <span>–</span>
            <input type="date" className="fld-i" value={customEnd} onChange={e => setCustomEnd(e.target.value)} />
          </div>
        )}

        <label className="fld-l">Basis</label>
        <div className="books-seg">
          <button type="button" className={`books-seg-btn${basis === 'cash' ? ' on' : ''}`}
            onClick={() => setBasis('cash')}>Cash</button>
          <button type="button" className={`books-seg-btn${basis === 'accrual' ? ' on' : ''}`}
            onClick={() => setBasis('accrual')}>Accrual</button>
        </div>

        {isSentio && report === 'profit_loss' && (
          <>
            <label className="fld-l">Project lens</label>
            <select className="fld-i books-select" value={projectId}
              onChange={e => setProjectId(e.target.value)} data-testid="books-project">
              <option value="">All projects</option>
              <option value="all">Breakdown by project</option>
              <option value="unassigned">Unassigned only</option>
              {(payload?.projects ?? []).map(p => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </>
        )}

        <div className="books-export">
          <button type="button" className="btn b-ol btn-sm"
            disabled={!activeCompany || exportBusy != null}
            onClick={() => handleExport('csv')} data-testid="books-export-csv">
            {exportBusy === 'csv'
              ? <><Loader2 size={13} className="books-spinner" /> Generating CSV…</>
              : <><FileSpreadsheet size={13} /> CSV</>}
          </button>
          <button type="button" className="btn b-ol btn-sm"
            disabled={!activeCompany || exportBusy != null}
            onClick={() => handleExport('pdf')} data-testid="books-export-pdf">
            {exportBusy === 'pdf'
              ? <><Loader2 size={13} className="books-spinner" /> Generating PDF…</>
              : <><Download size={13} /> PDF</>}
          </button>
        </div>
        {exportBusy === 'pdf' && (
          <p className="books-export-hint">PDF generation may take 20–30 seconds for large periods.</p>
        )}
        {exportErr && <p className="books-export-err">{exportErr}</p>}
      </div>

      {reportQ.isLoading && <div className="skel" style={{ height: 120, marginTop: 16 }} />}
      {reportQ.error && (
        <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 12 }}>
          {(reportQ.error as Error).message}
        </div>
      )}

      {payload && (
        <div className="books-table-wrap">
          {showCrosscheck && bkLimitPaused && (
            <div className="books-error-banner" data-testid="books-bk-paused-banner">
              <div className="books-preparing-title">
                {payload?.bkTrust?.label || 'Books sync paused for today'}
              </div>
              <div className="books-preparing-sub">
                Your Soren totals are still here. We will compare to your books again after the daily limit resets.
              </div>
            </div>
          )}
          {showCrosscheck && preparingBk && !bkLimitPaused && (
            <div className="books-preparing-banner" data-testid="books-preparing-banner">
              <Loader2 size={16} className="books-spinner" aria-hidden />
              <div>
                <div className="books-preparing-title">Checking your books</div>
                <div className="books-preparing-sub">
                  This can take up to a minute. Soren totals show right away; the books comparison runs in the background.
                </div>
              </div>
            </div>
          )}
          {showCrosscheck && bkError && !bkLimitPaused && (
            <div className="books-error-banner" data-testid="books-bk-error-banner">
              <div className="books-preparing-title">{payload?.bkTrust?.label || 'Books check unavailable'}</div>
              <div className="books-preparing-sub">
                {payload?.bkTrust?.error || 'Background warm has not completed for this period.'}
              </div>
            </div>
          )}
          <div className="books-meta mono">
            {payload.period?.label} · {payload.basis} · {payload.transactionCount} txns
          </div>
          <table className="books-table">
            <thead>
              <tr>
                <th>Category</th>
                <th className="num">Soren</th>
                <th className="num books-col-bk">{bkHeader}</th>
                {bkLoaded && <th className="num books-col-variance">Variance</th>}
              </tr>
            </thead>
            <tbody>
              {report === 'profit_loss' && displayRows.map((r, i) => (
                <ReportRow key={`${r.rowType}-${r.section}-${r.name}-${i}`} row={r} bkLoaded={bkLoaded} preparing={preparingBk} />
              ))}
              {report !== 'profit_loss' && (payload.rows ?? []).map(r => (
                <tr key={`${r.section}-${r.name}`}>
                  <td>{r.displayName || r.name}</td>
                  <td className="num mono">{money(r.aria)}</td>
                  <td className="num mono books-col-bk">
                    {bkLoaded ? money(r.bk) : preparingBk ? <BkSkeleton /> : '—'}
                  </td>
                  {bkLoaded && (
                    <td className={`num mono books-col-variance ${varianceClass(r.hasVariance)}`}>
                      {money(r.variance ?? (r.aria - r.bk))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {payload.byProject && payload.byProject.length > 0 && (
            <div className="books-by-project">
              <div className="fld-l" style={{ marginTop: 16 }}>By project</div>
              <table className="books-table">
                <thead>
                  <tr><th>Project</th><th className="num">Income</th><th className="num">Expense</th><th className="num">Net</th></tr>
                </thead>
                <tbody>
                  {payload.byProject.map(p => (
                    <tr key={String(p.projectId)}>
                      <td>{p.name}</td>
                      <td className="num mono">{money(p.income)}</td>
                      <td className="num mono">{money(p.expense)}</td>
                      <td className="num mono">{money(p.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function BkSkeleton() {
  return <span className="books-bk-skel" aria-hidden />
}

function ReportRow({ row, bkLoaded, preparing }: {
  row: BooksReportDisplayRow
  bkLoaded: boolean
  preparing: boolean
}) {
  const label = rowLabel(row)
  if (row.rowType === 'header') {
    return (
      <tr className="books-pl-header">
        <td colSpan={bkLoaded ? 4 : 3}><b>{label}</b></td>
      </tr>
    )
  }
  if (row.rowType === 'empty') {
    return (
      <tr className="books-pl-empty">
        <td colSpan={bkLoaded ? 4 : 3}><em>{label}</em></td>
      </tr>
    )
  }
  const isSubtotal = row.rowType === 'subtotal'
  const isTotal = row.rowType === 'total'
  const cls = isSubtotal || isTotal ? 'books-pl-subtotal' : ''
  const showVariance = bkLoaded && row.hasVariance && row.rowType === 'line'
  const showTotalVar = bkLoaded && row.hasVariance && (row.rowType === 'subtotal' || row.rowType === 'total')
  const bkCell = bkLoaded
    ? (isTotal || isSubtotal ? <b>{money(row.bk)}</b> : money(row.bk))
    : preparing ? <BkSkeleton /> : '—'
  return (
    <tr className={cls}>
      <td>{isTotal || isSubtotal ? <b>{label}</b> : label}</td>
      <td className="num mono">{isTotal || isSubtotal ? <b>{money(row.aria)}</b> : money(row.aria)}</td>
      <td className="num mono books-col-bk">{bkCell}</td>
      {bkLoaded && (showVariance || showTotalVar) && (
        <td className={`num mono books-col-variance ${varianceClass(row.hasVariance)}`}>
          {showTotalVar ? <b>{money(row.variance)}</b> : money(row.variance)}
        </td>
      )}
      {bkLoaded && !showVariance && !showTotalVar && <td className="num mono books-col-variance" />}
    </tr>
  )
}
